import { useContext, useEffect, useState, useCallback } from 'react';
import './style.css';

// Components
import SellModule from '../../organisms/sellModule/view';
import PayMethods from '../../organisms/paymethods/view';
import ServicesTable from '../../organisms/ServicesTable/view';
import ButtonGroup from '../../molecules/buttonGroup/view';
import Taxes from '../../organisms/taxes/view';
import NewService from '../../organisms/NewService/view';
import TabSwitcher from '../../molecules/TabSwitcher/view';
import Table from '../../molecules/tablas/view';
import CustomBox from '../../atoms/container/container';
import ButtonAction from '../../atoms/buttonAction/view';
import TooltipC from '../../atoms/tooltip/view';
import LoadingView from '../../pages/loading/loading-view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import Modal from '../../atoms/modal/view';
import ThirdForm from '../../organisms/thirdForm/view';
import InvoicesTable from '../../organisms/InvoicesTable/view';
import { Col, Row } from 'react-bootstrap';

// Context & Hooks
import { roleContext } from '../../../context/role/context';
import { listener, dispatchCustomEvent } from '../../../utils/dispatch';
import { useCarrito } from '../../../context/carrito/context';
import { useAskPrice } from '../../atoms/prompt/price';
import { useUpdateCarItem } from '../../atoms/prompt/caritem';
import { useNavigationIntent } from '../../../hooks/useNavigationIntent';
import { useOnboardingTour } from '../../../hooks/useOnboardingTour';
import { getThirdById, getThirdByIdentification, editThird } from '../../../usecases/thirds-usecase';

// Icons
import { BiErrorCircle } from 'react-icons/bi';
import { BsArrowRepeat } from 'react-icons/bs';

// Controller
import {
  getInvoiceList,
  getConflictedSalesList,
  handleResolveConflict,
  submitCreditNote,
} from './controller';

function ServicesPage() {
  useOnboardingTour();

  const { profile, isElectronicInvoiceEnable } = useContext(roleContext);
  const { askPrice, PriceModalComponent } = useAskPrice();
  const { askUpdateCarItem, UpdateCarItemModalComponent } = useUpdateCarItem();

  // Context Cart
  const {
    car,
    taxGroup,
    total,
    addItem,
    removeItem,
    updateItem: updateCartItem,
    cloneItem: cloneCartItem,
  } = useCarrito();

  // Local State
  const [tercero, setTercero] = useState(undefined);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const intent = useNavigationIntent();

  useEffect(() => {
    if (intent && intent.tabIndex !== undefined) {
      setActiveTabIndex(intent.tabIndex);
    }
  }, [intent]);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [invoices, setInvoices] = useState({
    data: [],
    page: 1,
    page_size: 8,
    total: 0,
  });
  const [conflicts, setConflicts] = useState({
    data: [],
    page: 1,
    page_size: 8,
    total: 0,
  });
  const [isLoadingConflicts, setIsLoadingConflicts] = useState(true);
  const [selectedThirdForConflict, setSelectedThirdForConflict] = useState(null);
  const [conflictBillId, setConflictBillId] = useState(null);

  // Callback para resolver conflictos
  const onResolveClick = async (row, force = false) => {
    const item = row._original || row;
    const conflictType = item.conflict_type;

    if (conflictType === 'CLIENT_DATA' && !force && (item.thirds_id || item.customer_id)) {
      setIsLoading(true);
      try {
        let thirdData = null;
        if (item.thirds_id) {
          thirdData = await getThirdById(item.thirds_id);
        } else if (item.customer_id) {
          thirdData = await getThirdByIdentification(item.customer_id);
        }

        if (thirdData) {
          setSelectedThirdForConflict(thirdData);
          setConflictBillId(item.bill_id);
          dispatchCustomEvent('modalconflict-edit-third-services');
          return;
        }
      } catch (err) {
        console.error('Error fetching third for conflict:', err);
      } finally {
        setIsLoading(false);
      }

      setSelectedThirdForConflict({
        id: item.thirds_id,
        identification: item.customer_id,
        name: item.customer,
      });
      setConflictBillId(item.bill_id);
      dispatchCustomEvent('modalconflict-edit-third-services');
      return;
    }

    setIsLoading(true);
    try {
      const result = await handleResolveConflict(item.bill_id, force);
      if (result?.requires_confirmation) {
        dispatchCustomEvent(
          'notification-confirm-services-resolve-conflict',
          result.requires_confirmation ? item : {}
        );
      } else if (result?.success) {
        handleFetchConflicts();
      }
    } catch (error) {
      console.error('Error resolving conflict:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConflictThirdSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const thirdId = formData.id || selectedThirdForConflict?.id;
      if (thirdId) {
        await editThird(thirdId, formData);
      }
      dispatchCustomEvent('modal-close-conflict-edit-third-services');
      dispatchCustomEvent('popup', {
        message: 'Cliente actualizado. Re-transmitiendo factura...',
        type: 'success',
      });

      if (conflictBillId) {
        const result = await handleResolveConflict(conflictBillId, true);
        if (result?.success) {
          handleFetchConflicts();
        }
      }
    } catch (err) {
      console.error('Error in conflict resolution with third:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Tab Facturas & Conflictos Logic (Servicios) ---
  const handleFetchInvoices = useCallback(
    async (page = invoices?.page || 1, limit = invoices?.page_size || 8) => {
      setInvoices((prev) => ({ ...prev, data: [] }));
      setIsLoadingInvoices(true);
      try {
        const result = await getInvoiceList(page, limit, {});
        setInvoices(result);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setIsLoadingInvoices(false);
      }
    },
    [invoices?.page, invoices?.page_size]
  );

  const handleFetchConflicts = useCallback(
    async (page = conflicts?.page || 1, limit = conflicts?.page_size || 8) => {
      setConflicts((prev) => ({ ...prev, data: [] }));
      setIsLoadingConflicts(true);
      try {
        const result = await getConflictedSalesList(page, limit);
        setConflicts(result);
      } catch (error) {
        console.error('Error fetching conflicts:', error);
      } finally {
        setIsLoadingConflicts(false);
      }
    },
    [conflicts?.page, conflicts?.page_size]
  );

  // --- Efectos ---
  useEffect(() => {
    const onThirdSelected = (e) =>
      setTercero(e.detail ? e.detail.data : undefined);
    const onAddProduct = (e) => addItem({ ...e.detail.data, is_service: true });
    const onCloneProduct = async (e) => {
      try {
        const precio = await askPrice(e.detail.data.value);
        cloneCartItem({ ...e.detail.data, is_service: true }, precio);
      } catch {
        /* Ignorar cancelación */
      }
    };

    const l1 = listener('btn-thirds-add', onThirdSelected);
    const l2 = listener('btn-add-product', onAddProduct);
    const l3 = listener('btn-clone-product', onCloneProduct);

    return () => {
      l1();
      l2();
      l3();
    };
  }, [addItem, askPrice, cloneCartItem]);

  useEffect(() => {
    const l1 = listener('reload-invoices', () => handleFetchInvoices());

    return () => {
      l1();
    };
  }, [handleFetchInvoices]);

  useEffect(() => {
    handleFetchConflicts();
  }, [handleFetchConflicts]);

  useEffect(() => {
    if (activeTabIndex === 2) handleFetchInvoices();
    if (activeTabIndex === 3) handleFetchConflicts();
  }, [activeTabIndex, handleFetchInvoices, handleFetchConflicts]);

  // --- Handlers de Carrito ---
  async function update(item, field = 'amount') {
    try {
      const isServiceItem = Boolean(item.is_service ?? (item.type === 'service' || true));
      const currentDescription = item.description !== undefined ? item.description : (item.name || '');
      const newData = await askUpdateCarItem(
        item.value,
        item.amount,
        field,
        item.name,
        currentDescription,
        isServiceItem
      );
      updateCartItem(item.auxsku, 'value', parseFloat(newData.value));
      updateCartItem(item.auxsku, 'amount', parseFloat(newData.amount));
      if (newData.description !== undefined) {
        updateCartItem(item.auxsku, 'description', newData.description);
      }
    } catch {
      /* Ignorar cancelación */
    }
  }

  const handleSubmitCreditNote = async (data) => {
    setIsLoading(true);
    try {
      await submitCreditNote(data);
      await handleFetchInvoices();
    } catch (error) {
      console.error('Error submitting credit note:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    {
      name: 'Facturación de Servicios',
      component: () => (
        <Row className="g-4 align-items-stretch">
          <Col md={6}>
            <CustomBox style={{ height: '100%', padding: 'var(--spacing-md)' }}>
              <ServicesTable profile={profile} type="sell" />
            </CustomBox>
          </Col>
          <Col md={6}>
            <CustomBox
              style={{
                height: '100%',
                padding: 0,
                border: 'none',
                background: 'transparent',
              }}
            >
              <SellModule
                removeItems={removeItem}
                car={car}
                update={update}
                taxGroup={taxGroup}
                tercero={tercero}
                total={total}
                showDescription={true}
              >
                <PayMethods origin="services" />
              </SellModule>
            </CustomBox>
          </Col>
        </Row>
      ),
    },
    {
      name: 'Catálogo de Servicios',
      component: () => (
        <>
          <ButtonGroup
            title="Catálogo de Servicios"
            description="Administración y configuración de tus servicios, tarifas e impuestos."
          >
            <Taxes />
            <NewService
              id="services-page"
              profile={profile}
            />
          </ButtonGroup>
          <br />
          <CustomBox>
            <ServicesTable
              profile={profile}
              type="stock"
            />
          </CustomBox>
        </>
      ),
    },
    {
      name: 'Facturas',
      disabled: !isElectronicInvoiceEnable,
      component: () => (
        <CustomBox style={{ maxHeight: '90%' }}>
          <InvoicesTable
            id="invoice-table-services"
            invoices={invoices}
            onReload={handleFetchInvoices}
            isLoading={isLoadingInvoices}
          />
        </CustomBox>
      ),
    },
    {
      name: 'Conflictos',
      badge: conflicts?.total || 0,
      disabled: false,
      component: () => (
        <CustomBox>
          <Table
            id="conflicts-table-services"
            onReload={() => handleFetchConflicts()}
            headersList={['Fecha', 'Cliente', 'Documento', 'Total', 'Motivo / Error']}
            paginatedData={conflicts}
            getFunction={handleFetchConflicts}
            excludeList={['bill_id', 'conflict_type', 'thirds_id', 'customer_id', '_original']}
            isLoading={isLoadingConflicts}
            formats={{
              total: { type: 'money' },
              date: { type: 'timestamp' },
              error_message: {
                type: 'custom',
                children: (item) => {
                  const msg = item.error_message || 'Sin detalle de error';
                  return (
                    <TooltipC text={msg}>
                      <div className="conflict-error-pill">
                        <BiErrorCircle size={15} className="conflict-error-icon" />
                        <span className="conflict-error-text">{msg}</span>
                      </div>
                    </TooltipC>
                  );
                },
              },
            }}
          >
            <ButtonAction
              title={<BsArrowRepeat />}
              onClick={(data) => onResolveClick(data)}
            />
          </Table>
        </CustomBox>
      ),
    },
  ];

  return (
    <>
      {isLoading && <LoadingView />}
      <ConfirmPopUp
        eventKey="create-nota-credito"
        msg={'Quieres anular la factura creando una nota credito?'}
        onAcept={handleSubmitCreditNote}
      />
      <ConfirmPopUp
        eventKey="services-resolve-conflict"
        msg={
          '¿Deseas generar la factura electrónica ahora con el siguiente consecutivo disponible?'
        }
        onAcept={(item) => onResolveClick(item, true)}
      />

      <Modal
        id="conflict-edit-third-services"
        title="Actualizar Datos del Cliente para Facturación"
      >
        {selectedThirdForConflict && (
          <ThirdForm
            initialData={selectedThirdForConflict}
            onSubmit={handleConflictThirdSubmit}
            isEditing={true}
          />
        )}
      </Modal>

      {PriceModalComponent}
      {UpdateCarItemModalComponent}

      <div className="services-page">
        <TabSwitcher
          tabs={tabs}
          onTabChange={setActiveTabIndex}
          activeTabIndex={activeTabIndex}
          hashKey="tab"
        />
      </div>
    </>
  );
}

export default ServicesPage;
